import type { FiltroPergunta } from '../types';

export interface SecaoFiltros {
  secao: string | null;
  perguntas: FiltroPergunta[];
}

interface Props {
  secoes: SecaoFiltros[];
  selecionados: Record<string, string[]>;
  abertos: Set<number>;
  expandidos: Set<number>;
  ativos: number;
  aoAlternarOpcao: (perguntaId: number, opcao: string) => void;
  aoAlternarBloco: (perguntaId: number) => void;
  aoExpandir: (perguntaId: number) => void;
  aoLimparTudo: () => void;
}

const MAX_OPCOES = 8;

export default function ColunaFiltros({
  secoes,
  selecionados,
  abertos,
  expandidos,
  ativos,
  aoAlternarOpcao,
  aoAlternarBloco,
  aoExpandir,
  aoLimparTudo,
}: Props) {
  return (
    <div>
      <div className="flex items-center justify-between gap-3">
        <span className="text-sm font-medium text-foreground-900">
          {ativos > 0
            ? `${ativos} ${ativos === 1 ? 'filtro ativo' : 'filtros ativos'}`
            : 'Nenhum filtro'}
        </span>
        {ativos > 0 && (
          <button
            type="button"
            onClick={aoLimparTudo}
            className="cursor-pointer whitespace-nowrap text-sm font-medium text-primary-600 transition-colors hover:text-primary-700"
          >
            Limpar tudo
          </button>
        )}
      </div>

      {secoes.length === 0 ? (
        <p className="mt-4 text-sm text-secondary-500">
          Esta ficha não tem perguntas de escolha para filtrar.
        </p>
      ) : (
        <div className="mt-4 space-y-6">
          {secoes.map((secao, i) => (
            <div key={secao.secao ?? `secao-${i}`}>
              {secao.secao && (
                <h4 className="text-xs font-medium uppercase tracking-wide text-secondary-500">
                  {secao.secao}
                </h4>
              )}
              <div className={`${secao.secao ? 'mt-2' : ''} space-y-3`}>
                {secao.perguntas.map((pergunta) => (
                  <BlocoFiltro
                    key={pergunta.pergunta_id}
                    pergunta={pergunta}
                    aberto={abertos.has(pergunta.pergunta_id)}
                    expandido={expandidos.has(pergunta.pergunta_id)}
                    selecionadas={selecionados[String(pergunta.pergunta_id)] ?? []}
                    aoAlternarOpcao={aoAlternarOpcao}
                    aoAlternarBloco={aoAlternarBloco}
                    aoExpandir={aoExpandir}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function BlocoFiltro({
  pergunta,
  aberto,
  expandido,
  selecionadas,
  aoAlternarOpcao,
  aoAlternarBloco,
  aoExpandir,
}: {
  pergunta: FiltroPergunta;
  aberto: boolean;
  expandido: boolean;
  selecionadas: string[];
  aoAlternarOpcao: (perguntaId: number, opcao: string) => void;
  aoAlternarBloco: (perguntaId: number) => void;
  aoExpandir: (perguntaId: number) => void;
}) {
  const opcoesVisiveis = expandido ? pergunta.opcoes : pergunta.opcoes.slice(0, MAX_OPCOES);
  const restantes = pergunta.opcoes.length - MAX_OPCOES;

  return (
    <div className="rounded-lg border border-secondary-200 bg-background-50">
      <button
        type="button"
        onClick={() => aoAlternarBloco(pergunta.pergunta_id)}
        className="flex w-full cursor-pointer items-center justify-between gap-2 px-3 py-2.5 text-left"
      >
        <span className="text-sm font-medium text-foreground-900">{pergunta.enunciado}</span>
        <span className="flex h-4 w-4 flex-shrink-0 items-center justify-center text-secondary-400">
          {aberto ? (
            <i className="ri-arrow-up-s-line text-lg" />
          ) : (
            <i className="ri-arrow-down-s-line text-lg" />
          )}
        </span>
      </button>

      {aberto && (
        <div className="border-t border-secondary-100 px-1.5 py-1.5">
          {opcoesVisiveis.map((opcao) => {
            const marcada = selecionadas.includes(opcao.opcao);
            return (
              <label
                key={opcao.opcao}
                className="flex cursor-pointer items-center gap-2.5 rounded-md px-2 py-1.5 transition-colors hover:bg-background-100"
              >
                <input
                  type="checkbox"
                  checked={marcada}
                  onChange={() => aoAlternarOpcao(pergunta.pergunta_id, opcao.opcao)}
                  className="h-4 w-4 cursor-pointer rounded accent-primary-500"
                />
                <span className="min-w-0 flex-1 truncate text-sm text-foreground-800">
                  {opcao.opcao}
                </span>
                <span className="shrink-0 text-xs text-secondary-400">{opcao.quantos}</span>
              </label>
            );
          })}

          {pergunta.opcoes.length > MAX_OPCOES && (
            <button
              type="button"
              onClick={() => aoExpandir(pergunta.pergunta_id)}
              className="mt-1 cursor-pointer whitespace-nowrap px-2 py-1 text-sm font-medium text-primary-600 transition-colors hover:text-primary-700"
            >
              {expandido ? 'Ver menos' : `Ver todas (${restantes} a mais)`}
            </button>
          )}
        </div>
      )}
    </div>
  );
}